import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

const LABELS = {
  bureau: "Bureau score (CIBIL)",
  cashflow: "Cash-flow / bank statement",
  income_stability: "Income & employment stability",
  foir: "FOIR / obligations",
  vehicle: "Vehicle quality (CarDr grade, age, km)",
  fraud_doc: "Fraud & document risk",
  source_quality: "Dealer / DSA source quality",
  residence_stability: "Residence stability",
};

export default function ScorecardTab() {
  const [weights, setWeights] = useState(null);
  const [version, setVersion] = useState(1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/policy/scorecard").then(r => { setWeights(r.data.weights); setVersion(r.data.version); }).catch(() => toast.error("Could not load scorecard"));
  }, []);

  if (!weights) return <div className="text-sm text-slate-500">Loading scorecard…</div>;

  const total = Object.values(weights).reduce((a, b) => a + Number(b || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      const numeric = Object.fromEntries(Object.entries(weights).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.put("/policy/scorecard", { weights: numeric, reason });
      setWeights(data.weights); setVersion(data.version); setReason("");
      toast.success(`Scorecard saved — v${data.version}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="scorecard-tab">
      <Card className="p-5 border-slate-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-sm font-semibold text-slate-900">CarFin Risk Score component weights</div>
            <div className="text-xs text-slate-500">v{version} · weights must total exactly 100. Each component is scored 0–100 and weighted into the 0–1000 CarFin score.</div>
          </div>
          <div className={`text-sm font-mono font-semibold px-3 py-1 rounded-full ${Math.abs(total - 100) < 0.01 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`} data-testid="scorecard-total">
            Total: {total}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {Object.entries(weights).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <div className="flex-1 text-sm text-slate-700">{LABELS[k] || k}</div>
              <Input type="number" className="w-24" value={v} onChange={e => setWeights(w => ({ ...w, [k]: e.target.value }))} data-testid={`scorecard-weight-${k}`} />
              <div className="w-28 h-1.5 rounded-full bg-slate-100">
                <div className="h-1.5 rounded-full bg-teal-500" style={{ width: `${Math.min(100, Number(v) * 2.5)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-5 border-teal-200 bg-teal-50/50">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-64">
            <Label className="text-xs">Reason for change</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} className="mt-1 bg-white" data-testid="scorecard-reason" />
          </div>
          <Button onClick={save} disabled={busy || Math.abs(total - 100) > 0.01} className="bg-teal-600 hover:bg-teal-700" data-testid="scorecard-save-btn">Save scorecard</Button>
        </div>
      </Card>
    </div>
  );
}
